import React, { useEffect, useState } from 'react';
import Topbar from '../components/layout/Topbar';
import { useAuth } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import { tenantsApi, refApi, formatApiError } from '../lib/api';

function Badge({ children, color }) {
  return (
    <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background: `${color}22`, color }}>
      {children}
    </span>
  );
}

export default function Tenants() {
  const { country, countryCode } = useCountry();
  const { profile } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    nom: '', type: 'mairie', plan: 'starter', email_admin: '', telephone: '', actif: true, country_mode: countryCode,
    commune_id: '', commune_nom: '', max_agents: '',
    admin_full_name: '', admin_email: '', admin_password: '', admin_role: 'admin_commune',
  });
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [communes, setCommunes] = useState([]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await tenantsApi.list();
      setTenants(data || []);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!showModal || editing) return;
    (async () => {
      try {
        const { data } = await refApi.communes(form.country_mode);
        setCommunes(data || []);
      } catch { setCommunes([]); }
    })();
  }, [showModal, editing, form.country_mode]);

  const openCreate = () => {
    setEditing(null);
    setForm({ nom: '', type: 'mairie', plan: 'starter', email_admin: '', telephone: '', actif: true, country_mode: countryCode,
      commune_id: '', commune_nom: '', max_agents: '',
      admin_full_name: '', admin_email: '', admin_password: '', admin_role: 'admin_commune' });
    setShowModal(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      nom: t.nom || '', type: t.type || 'mairie', plan: t.plan || 'starter',
      email_admin: t.email_admin || '', telephone: t.telephone || '',
      actif: t.actif !== false, country_mode: t.country_mode || 'quebec',
      commune_id: t.commune_id || '', commune_nom: t.commune_nom || '',
      max_agents: t.max_agents ?? '',
      admin_full_name: '', admin_email: '', admin_password: '', admin_role: 'admin_commune',
    });
    setShowModal(true);
  };

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    setError('');
    setNotice('');
    const payload = {
      nom: form.nom.trim(),
      type: form.type,
      plan: form.plan,
      country_mode: form.country_mode,
      commune_id: form.commune_id || null,
      commune_nom: form.commune_nom || null,
      max_agents: form.max_agents === '' ? null : parseInt(form.max_agents, 10),
      email_admin: form.email_admin.trim() || null,
      telephone: form.telephone.trim() || null,
      actif: form.actif,
    };
    if (!editing && form.admin_email.trim()) {
      payload.admin_full_name = form.admin_full_name.trim() || null;
      payload.admin_email = form.admin_email.trim();
      payload.admin_password = form.admin_password;
      payload.admin_role = form.admin_role;
    }
    try {
      let created;
      if (editing) {
        const { data } = await tenantsApi.update(editing.id, payload);
        created = data;
      } else {
        const { data } = await tenantsApi.create(payload);
        created = data;
      }
      if (created && !editing) {
        setTenants((prev) => [created, ...prev]);
        if (created.admin_user) {
          setNotice(`Tenant créé avec son administrateur : ${created.admin_user.email} (mot de passe temporaire à changer à la 1ʳᵉ connexion).`);
        }
      } else if (created && editing) {
        setTenants((prev) => prev.map((t) => (t.id === editing.id ? { ...t, ...created } : t)));
      }
      setShowModal(false);
      setEditing(null);
      await load();
    } catch (ex) {
      setError(formatApiError(ex));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Supprimer définitivement le tenant « ${t.nom} » ?\nCette action est irréversible et supprimera aussi les utilisateurs rattachés.`)) return;
    setError('');
    try {
      await tenantsApi.remove(t.id);
      setTenants((prev) => prev.filter((x) => x.id !== t.id));
    } catch (e) { setError(formatApiError(e)); }
  };

  const copyCitizenLink = (t) => {
    if (!t.commune_id) { setError("Ce tenant n'a pas de commune associée — éditez-le pour en choisir une."); return; }
    const link = `${window.location.origin}/signaler/${t.commune_id}`;
    navigator.clipboard?.writeText(link).then(() => setNotice(`🔗 Lien citoyen copié : ${link}`)).catch(() => setNotice(link));
  };

  const toggleActif = async (t) => {
    try {
      await tenantsApi.update(t.id, { actif: !t.actif });
      load();
    } catch (e) { setError(formatApiError(e)); }
  };

  if (profile?.role !== 'superadmin') {
    return (
      <div className="h-screen flex flex-col">
        <Topbar />
        <div className="flex-1 flex items-center justify-center text-gray-500">Accès réservé aux super-administrateurs.</div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-[#F4F6F9]" data-testid="tenants-page">
      <Topbar />
      <div className="px-6 py-4 flex items-center justify-between bg-white border-b border-gray-200">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Gestion des tenants</h1>
          <p className="text-xs text-gray-500 mt-0.5">Créez et gérez les organisations clientes (mairies, régions, ONG, bailleurs).</p>
        </div>
        <button onClick={openCreate} className="px-4 py-2 rounded-lg text-white text-sm font-semibold transition" style={{ background: country.palette.primary }} data-testid="tenants-new-btn">
          + Nouveau tenant
        </button>
      </div>

      <main className="flex-1 overflow-y-auto p-6">
        {notice && <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg px-4 py-3 mb-4 text-sm" data-testid="tenants-notice">✅ {notice}</div>}
        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-4 text-sm">{error}</div>}
        {loading ? (
          <div className="text-center text-sm text-gray-500 py-10">Chargement…</div>
        ) : tenants.length === 0 ? (
          <div className="text-center text-sm text-gray-500 py-10 bg-white rounded-xl border border-gray-200">Aucun tenant. Cliquez sur "Nouveau tenant" pour en créer un.</div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="text-left px-4 py-3">Nom</th>
                  <th className="text-left px-4 py-3">Type</th>
                  <th className="text-left px-4 py-3">Plan</th>
                  <th className="text-left px-4 py-3">Pays</th>
                  <th className="text-left px-4 py-3">Commune</th>
                  <th className="text-left px-4 py-3">Email admin</th>
                  <th className="text-left px-4 py-3">Statut</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((t) => (
                  <tr key={t.id} className="border-t border-gray-100" data-testid={`tenant-row-${t.id}`}>
                    <td className="px-4 py-3 font-medium text-gray-900">{t.nom}</td>
                    <td className="px-4 py-3 text-gray-600">{t.type}</td>
                    <td className="px-4 py-3"><Badge color={t.plan === 'enterprise' ? '#7B1FA2' : t.plan === 'pro' ? '#0057A8' : '#6b7c6e'}>{t.plan}</Badge></td>
                    <td className="px-4 py-3"><Badge color={t.country_mode === 'senegal' ? '#00853F' : '#002855'}>{t.country_mode === 'senegal' ? '🌍 Sénégal' : '🍁 Québec'}</Badge></td>
                    <td className="px-4 py-3 text-gray-700 text-xs">{t.commune_nom || '—'}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{t.email_admin || '—'}</td>
                    <td className="px-4 py-3"><Badge color={t.actif ? '#1B5E20' : '#B71C1C'}>{t.actif ? 'Actif' : 'Inactif'}</Badge></td>
                    <td className="px-4 py-3 text-right space-x-1">
                      <button onClick={() => openEdit(t)} className="text-xs px-2 py-1 rounded hover:bg-gray-100" data-testid={`tenant-edit-${t.id}`}>✏️ Éditer</button>
                      <button onClick={() => toggleActif(t)} className="text-xs px-2 py-1 rounded hover:bg-gray-100" data-testid={`tenant-toggle-${t.id}`}>{t.actif ? '🚫 Désactiver' : '✅ Activer'}</button>
                      <button onClick={() => copyCitizenLink(t)} className="text-xs px-2 py-1 rounded hover:bg-gray-100" data-testid={`tenant-link-${t.id}`}>🔗 Lien citoyen</button>
                      <button onClick={() => remove(t)} className="text-xs px-2 py-1 rounded text-red-600 hover:bg-red-50" data-testid={`tenant-delete-${t.id}`}>🗑️ Supprimer</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="tenant-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-semibold">{editing ? 'Éditer le tenant' : 'Nouveau tenant'}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-900" data-testid="tenant-modal-close">✕</button>
            </div>
            <form onSubmit={save} className="px-6 py-5 space-y-3 max-h-[70vh] overflow-y-auto">
              {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm" data-testid="tenant-form-error">⚠️ {error}</div>}
              <div>
                <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Nom *</label>
                <input required type="text" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border-2 border-gray-200 text-sm outline-none focus:border-blue-600" data-testid="tenant-form-nom" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Type</label>
                  <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border-2 border-gray-200 text-sm" data-testid="tenant-form-type">
                    <option value="mairie">Mairie / Commune</option>
                    <option value="region">Région</option>
                    <option value="ong">ONG</option>
                    <option value="bailleur">Bailleur</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Plan</label>
                  <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border-2 border-gray-200 text-sm" data-testid="tenant-form-plan">
                    <option value="starter">Starter</option>
                    <option value="pro">Pro</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Pays (mode)</label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setForm({ ...form, country_mode: 'quebec', commune_id: '', commune_nom: '' })} className={`flex-1 px-3 py-2.5 rounded-lg border-2 text-sm transition ${form.country_mode === 'quebec' ? 'border-blue-600 bg-blue-50 font-semibold' : 'border-gray-200'}`} data-testid="tenant-form-country-quebec">🍁 Québec</button>
                  <button type="button" onClick={() => setForm({ ...form, country_mode: 'senegal', commune_id: '', commune_nom: '' })} className={`flex-1 px-3 py-2.5 rounded-lg border-2 text-sm transition ${form.country_mode === 'senegal' ? 'border-green-600 bg-green-50 font-semibold' : 'border-gray-200'}`} data-testid="tenant-form-country-senegal">🌍 Sénégal</button>
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{form.country_mode === 'senegal' ? 'Commune' : 'Municipalité'}{!editing && <span className="text-red-500"> *</span>}</label>
                <select
                  value={form.commune_id}
                  required={!editing}
                  onChange={(e) => {
                    const c = communes.find((x) => x.id === e.target.value);
                    setForm({ ...form, commune_id: e.target.value, commune_nom: c?.nom || '' });
                  }}
                  className="w-full px-3 py-2.5 rounded-lg border-2 border-gray-200 text-sm outline-none focus:border-blue-600"
                  data-testid="tenant-form-commune"
                >
                  <option value="">— Sélectionner —</option>
                  {communes.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Limite d'agents <span className="font-normal lowercase text-gray-400">(vide = illimité)</span></label>
                <input type="number" min="0" value={form.max_agents} onChange={(e) => setForm({ ...form, max_agents: e.target.value })} placeholder="Illimité" className="w-full px-3 py-2.5 rounded-lg border-2 border-gray-200 text-sm outline-none focus:border-blue-600" data-testid="tenant-form-max-agents" />
              </div>
              {editing ? (
                <div>
                  <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Email admin</label>
                  <input type="email" value={form.email_admin} onChange={(e) => setForm({ ...form, email_admin: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border-2 border-gray-200 text-sm outline-none focus:border-blue-600" data-testid="tenant-form-email" />
                </div>
              ) : (
                <div className="rounded-xl border-2 border-dashed border-gray-200 p-4 space-y-3 bg-gray-50/50">
                  <div className="text-[11px] font-bold text-gray-600 uppercase tracking-wide">👤 Administrateur du tenant <span className="font-normal lowercase text-gray-400">(optionnel)</span></div>
                  <p className="text-[11px] text-gray-500 -mt-1">Renseignez ces champs pour créer le compte admin en même temps. Il devra changer son mot de passe à la 1ʳᵉ connexion.</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Nom complet</label>
                      <input type="text" value={form.admin_full_name} onChange={(e) => setForm({ ...form, admin_full_name: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border-2 border-gray-200 text-sm outline-none focus:border-blue-600" data-testid="tenant-form-admin-name" />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Rôle</label>
                      <select value={form.admin_role} onChange={(e) => setForm({ ...form, admin_role: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border-2 border-gray-200 text-sm" data-testid="tenant-form-admin-role">
                        <option value="admin_commune">Admin commune</option>
                        <option value="admin_region">Admin région</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Email de connexion</label>
                    <input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} placeholder="admin@organisation.com" className="w-full px-3 py-2.5 rounded-lg border-2 border-gray-200 text-sm outline-none focus:border-blue-600" data-testid="tenant-form-admin-email" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Mot de passe temporaire {form.admin_email.trim() && <span className="text-red-500">*</span>}</label>
                    <input type="text" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} placeholder="min. 6 caractères" minLength={6} required={!!form.admin_email.trim()} className="w-full px-3 py-2.5 rounded-lg border-2 border-gray-200 text-sm outline-none focus:border-blue-600" data-testid="tenant-form-admin-password" />
                  </div>
                </div>
              )}
              <div>
                <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Téléphone</label>
                <input type="tel" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border-2 border-gray-200 text-sm outline-none focus:border-blue-600" data-testid="tenant-form-telephone" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="actif" checked={form.actif} onChange={(e) => setForm({ ...form, actif: e.target.checked })} data-testid="tenant-form-actif" />
                <label htmlFor="actif" className="text-sm text-gray-700">Tenant actif</label>
              </div>
              <div className="flex gap-2 pt-3">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 text-sm hover:bg-gray-50" data-testid="tenant-form-cancel">Annuler</button>
                <button type="submit" disabled={saving} className="flex-1 px-4 py-2.5 rounded-lg text-white text-sm font-semibold disabled:opacity-50" style={{ background: country.palette.primary }} data-testid="tenant-form-save">
                  {saving ? '…' : editing ? 'Enregistrer' : 'Créer le tenant'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
